import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import {
  PaymentTransactionHistory,
  CardDetails,
  BillingDetails,
  isPaymentTransactionAmountInclusiveOfAddons,
  resolvePaymentTransactionDiscount,
  sumPaymentTransactionAddonAmount,
} from './payment.service';
import { Order } from '../orders/models/order.models';
import { ServiceRecord } from '../components/service-history/service-history.component';
import { PhoneValidator } from '../shared/validators/phone.validator';
import { PaymentMethodCode } from '../shared/types/payment-method.enum';
import { formatDateTimeInTimeZone, getUserTimezone } from '../shared/utils/date.util';
import {
  formatCardPaymentLabel,
  resolvePaymentReceiptCard,
} from '../shared/utils/card-payment-label.util';
import {
  hasPromoMessageContent,
  parsePromoMessageLines,
} from '../shared/utils/receipt-promo-message.util';
import {
  RAINCHECK_MESSAGE,
  shouldShowRaincheckMessage,
} from '../shared/utils/raincheck-policy.util';

export interface InvoiceData {
  order: Order;
  transaction: PaymentTransactionHistory;
  customerInfo: {
    name: string;
    email: string;
    phone: string;
    address?: string;
  };
  businessInfo: {
    name: string;
    address: string;
    phone: string;
    email: string;
  };
  cardDetails?: CardDetails | null;
  billingDetails?: BillingDetails | null;
  /** Order location IANA timezone; falls back to staff shop timezone when omitted. */
  receiptTimezone?: string | null;
}

/** Brand colours shared across all generated PDF documents. */
const PDF_PRIMARY_COLOR = '#101123';
const PDF_SECONDARY_COLOR = '#717479';
const PDF_ACCENT_COLOR = '#077258';
const PDF_LIGHT_GRAY = '#F8F9FA';

/**
 * Service for generating and downloading professionally formatted invoice PDFs (standard invoices and service-history invoices) using jsPDF. Handles document initialization, header/footer, customer and payment sections, itemized tables, discounts, taxes, totals, and filename generation before triggering a save/download.
 * @component
 * @selector app-pdf-service
 * @example
 *   // Inject the service into a component and call:
 *   // constructor(private pdfService: PdfService) {}
 *   // this.pdfService.generateInvoice(invoiceData);
 * @input InvoiceData invoiceData - Object containing businessInfo, order, transaction, customerInfo, and optional cardDetails; used by generateInvoice() to build and download an invoice PDF.
 * @input ServiceRecord serviceRecord - Record with id, date, packageName, price, vehicle, and location; used by generateServiceHistoryInvoice() to build and download a service history invoice PDF.
 * @input { name: string; email: string; phone: string; address?: string } customerInfo - Customer contact info used by generateServiceHistoryInvoice().
 * @output void download - Side-effect: triggers the browser to save a generated .pdf file. Methods return void and do not emit events.
 *
 * Key methods:
 * - generateInvoice(invoiceData: InvoiceData): void
 *   Generates a full invoice PDF (business header, billing block, services table, discounts, taxes, totals, payment info) and saves it as a .pdf file using a filename derived from the order and payment IDs.
 * - generateServiceHistoryInvoice(serviceRecord: ServiceRecord, customerInfo): void
 *   Generates a service-history style invoice for a single service record and downloads it as a .pdf file.
 * - initializePDFDocument(): { doc, pageWidth, pageHeight, primaryColor, secondaryColor, accentColor, lightGray }
 *   Creates and returns a configured jsPDF instance and common layout metrics/colors.
 * - renderTableHeader(doc: jsPDF, pageWidth: number, tableStartY: number): number
 *   Renders the two-column table header (DESCRIPTION / AMOUNT), resets fonts/colors for table body, and returns the Y coordinate for the first row.
 * - formatDate(dateString: string): string
 *   Formats an ISO date string into a human-readable 'Month Day, Year' string.
 * - formatAmountCents(amountInCents: string | number): string
 *   Normalizes and formats numeric amounts as dollar strings (e.g. "$12.34").
 * - formatPaymentMethod(paymentMethod: string): string
 *   Normalizes and humanizes payment method identifiers (e.g. "card_present" -> "Card").
 * - capitalizeFirst(text: string): string
 *   Utility to capitalize a single word.
 *
 * Important notes / lifecycle:
 * - This is an Angular @Injectable providedIn: 'root' service (no component lifecycle hooks).
 * - Methods perform synchronous PDF construction and immediately call doc.save(filename) to prompt download; they return void.
 * - Input objects should contain the expected shape (businessInfo, transaction, order, customerInfo, cardDetails) to ensure fields like phone, email, orderId, and payment_intent_id are available for filename generation and displayed content.
 */
@Injectable({
  providedIn: 'root',
})
export class PdfService {
  constructor() {}

  /**
   * Renders the two-column table header (DESCRIPTION / AMOUNT) and resets the font
   * for table-body rows. Returns the Y coordinate to use for the first content row.
   */
  private renderTableHeader(doc: jsPDF, pageWidth: number, tableStartY: number): number {
    doc.setFillColor(PDF_ACCENT_COLOR);
    doc.rect(20, tableStartY, pageWidth - 40, 12, 'F');
    doc.setFontSize(10);
    doc.setTextColor('white');
    doc.setFont('helvetica', 'bold');
    doc.text('DESCRIPTION', 25, tableStartY + 8);
    doc.text('AMOUNT', pageWidth - 25, tableStartY + 8, { align: 'right' });
    doc.setTextColor(PDF_PRIMARY_COLOR);
    doc.setFont('helvetica', 'normal');
    return tableStartY + 20;
  }

  private initializePDFDocument() {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const primaryColor = PDF_PRIMARY_COLOR;
    const secondaryColor = PDF_SECONDARY_COLOR;
    const accentColor = PDF_ACCENT_COLOR;
    const lightGray = PDF_LIGHT_GRAY;
    return { doc, pageWidth, pageHeight, primaryColor, secondaryColor, accentColor, lightGray };
  }

  /**
   * Generate a PDF invoice from the given invoice data and save it as a downloadable file.
   * @example
   * enenerateInvoice(sampleInvoice)
   * saves PDF file and returns void
   * @param {{InvoiceData}} {{invoiceData}} - Invoice data including businessInfo, customerInfo, transaction, order, and optional cardDetails used to populate the PDF.
   * @returns {{void}} No return value; the function saves the generated invoice PDF to the user's device.
   **/
  generateInvoice(invoiceData: InvoiceData): void {
    const { doc, filename } = this.buildInvoiceDocument(invoiceData);
    this.outputPdfForDownload(doc, filename);
  }

  /**
   * Builds and outputs the receipt PDF for printing.
   * @returns `true` when the print target was opened/handed off, `false` when the browser
   * blocked the print window (e.g. pop-up blocker) so callers can surface a real failure.
   */
  printInvoice(invoiceData: InvoiceData): boolean {
    const doc = this.buildReceiptDocument(invoiceData);
    return this.outputPdfForPrint(doc, 'receipt.pdf');
  }

  private isAndroidWebView(): boolean {
    return typeof (window as any).TapToPayAndroid !== 'undefined';
  }

  private outputPdfForDownload(doc: jsPDF, filename: string): void {
    if (this.isAndroidWebView()) {
      const dataUri = doc.output('datauristring') as string;
      const base64 = dataUri.split(',')[1];
      (window as any).TapToPayAndroid.downloadPdf(base64, filename);
    } else {
      doc.save(filename);
    }
  }

  /**
   * Outputs the PDF to the platform print target.
   * @returns `true` when the print window/native handoff succeeded, `false` when the browser
   * blocked opening the print window (so the print did not actually happen).
   */
  private outputPdfForPrint(doc: jsPDF, filename: string): boolean {
    if (this.isAndroidWebView()) {
      const dataUri = doc.output('datauristring') as string;
      const base64 = dataUri.split(',')[1];
      (window as any).TapToPayAndroid.printPdf(base64, filename);
      return true;
    }
    doc.autoPrint();
    const printWindow = window.open(doc.output('bloburl'), '_blank');
    return printWindow !== null;
  }

  /**
   * Expands intentional promo lines (from `\n` / real newlines) and wraps any that exceed
   * the box width. Must stay in sync with measure/renderPromoMessageBox.
   */
  private expandPromoMessageLines(doc: jsPDF, message: string, maxTextWidth: number): string[] {
    const intentional = parsePromoMessageLines(message);
    const lines: string[] = [];
    for (const line of intentional) {
      if (line === '') {
        lines.push('');
        continue;
      }
      const wrapped = doc.splitTextToSize(line, maxTextWidth) as string[];
      lines.push(...(wrapped.length > 0 ? wrapped : [line]));
    }
    return lines;
  }

  /**
   * Shared promo-box layout (wrap + height). Must stay in sync with renderPromoMessageBox.
   * Returns height consumed including the gap below the box.
   */
  private measurePromoMessageBoxHeight(
    doc: jsPDF,
    message: string,
    width: number,
    fontSize: number
  ): number {
    const isReceipt = fontSize < 8;
    const padX = isReceipt ? 2.2 : 4.5;
    const padY = isReceipt ? 2.2 : 4.5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize);

    const textH = doc.getTextDimensions('Ag').h;
    const lineHeight = textH * 1.2;
    const maxTextWidth = Math.max(10, width - padX * 2);
    const lines = this.expandPromoMessageLines(doc, message, maxTextWidth);
    if (lines.length === 0) return 0;
    const boxHeight = padY * 2 + lines.length * lineHeight;
    return boxHeight + (isReceipt ? 5 : 8);
  }

  /** Dashed promo callout matching the receipt design; returns Y below the box. */
  private renderPromoMessageBox(
    doc: jsPDF,
    message: string,
    x: number,
    y: number,
    width: number,
    fontSize: number
  ): number {
    const isReceipt = fontSize < 8;
    const padX = isReceipt ? 2.2 : 4.5;
    const padY = isReceipt ? 2.2 : 4.5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize);

    const textH = doc.getTextDimensions('Ag').h;
    const lineHeight = textH * 1.2;
    const maxTextWidth = Math.max(10, width - padX * 2);
    const lines = this.expandPromoMessageLines(doc, message, maxTextWidth);
    if (lines.length === 0) return y;

    const textBlockHeight = lines.length * lineHeight;
    const boxHeight = padY * 2 + textBlockHeight;
    const textBlockTop = y + padY;
    const centerX = x + width / 2;

    doc.setDrawColor(PDF_PRIMARY_COLOR);
    doc.setLineWidth(isReceipt ? 0.18 : 0.22);
    doc.setLineDashPattern([1, 1.1], 0);
    doc.roundedRect(x, y, width, boxHeight, 1.5, 1.5, 'S');
    doc.setLineDashPattern([], 0);

    doc.setTextColor(PDF_PRIMARY_COLOR);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize);
    let textY = textBlockTop;
    for (const line of lines) {
      if (line !== '') {
        doc.text(line, centerX, textY, { align: 'center', baseline: 'top' });
      }
      textY += lineHeight;
    }

    return y + boxHeight + (isReceipt ? 5 : 8);
  }

  private buildReceiptDocument(invoiceData: InvoiceData): jsPDF {
    const safe = (v: string | null | undefined, fallback = ''): string =>
      v != null && String(v).trim() !== '' ? String(v) : fallback;

    const W = 80;
    const ML = 4;
    const MR = W - 4;
    const CX = W / 2;
    const promoWidth = MR - ML;
    const promoFontSize = 5.5;

    const tx = invoiceData.transaction;
    const addonRows = Array.isArray(tx.addons) ? tx.addons : [];
    // Hoisted above lineCount because the discount is now derived from the subtotal: it is the
    // gap between what was ordered and what this payment actually collected, so the printed
    // lines always reconcile with the printed TOTAL.
    const amountStr = String(tx.amount || '0');
    const baseAmount = isFinite(Number(amountStr)) ? Number(amountStr) : 0;
    const addonSum = sumPaymentTransactionAddonAmount(tx);
    const amountIncludesAddons = isPaymentTransactionAmountInclusiveOfAddons(tx);
    const packagePortion = amountIncludesAddons
      ? Math.max(0, baseAmount - addonSum)
      : Math.max(0, baseAmount);
    const subtotalBeforeDiscount = amountIncludesAddons ? baseAmount : baseAmount + addonSum;
    const discountNum = resolvePaymentTransactionDiscount(tx, subtotalBeforeDiscount);
    const promoMessage = safe(tx.promo_message);
    const showPromo = hasPromoMessageContent(promoMessage);
    const showRaincheck = shouldShowRaincheckMessage(invoiceData);
    const lineCount = 3 + addonRows.length + (discountNum > 0 ? 1 : 0);

    // Probe wrap/height with the same font/width math as renderPromoMessageBox.
    let promoHeight = 0;
    if (showPromo) {
      const probe = new jsPDF({ orientation: 'p', unit: 'mm', format: [W, 50] });
      promoHeight = this.measurePromoMessageBoxHeight(
        probe,
        promoMessage,
        promoWidth,
        promoFontSize
      );
    }
    // The page height is fixed at construction, so the raincheck footer line has to be
    // reserved here or it prints past the end of the receipt (CWS-1523).
    let raincheckHeight = 0;
    if (showRaincheck) {
      const probe = new jsPDF({ orientation: 'p', unit: 'mm', format: [W, 50] });
      probe.setFont('helvetica', 'bold');
      probe.setFontSize(7);
      const wrapped = probe.splitTextToSize(RAINCHECK_MESSAGE, promoWidth) as string[];
      raincheckHeight = Math.max(1, wrapped.length) * 4 + 1;
    }
    const estimatedHeight = 130 + lineCount * 7 + promoHeight + raincheckHeight;

    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: [W, estimatedHeight] });

    let y = 6;

    const divider = () => {
      doc.setDrawColor(PDF_SECONDARY_COLOR);
      doc.setLineWidth(0.3);
      doc.line(ML, y, MR, y);
      y += 4;
    };

    // Business name
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(PDF_PRIMARY_COLOR);
    doc.text(safe(invoiceData.businessInfo.name, 'Car Wash'), CX, y, { align: 'center' });
    y += 5;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(PDF_SECONDARY_COLOR);
    doc.text(PhoneValidator.formatUsaPhone(safe(invoiceData?.businessInfo?.phone ?? '')), CX, y, {
      align: 'center',
    });
    y += 4;
    doc.text(safe(invoiceData.businessInfo.email), CX, y, { align: 'center' });
    y += 5;

    divider();

    // Invoice heading
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(PDF_PRIMARY_COLOR);
    doc.text('INVOICE', CX, y, { align: 'center' });
    y += 5;

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(PDF_PRIMARY_COLOR);
    const rawOrder = invoiceData.order as any;
    const rawOrderId = invoiceData.order.orderId || rawOrder.order_id || rawOrder.id || '';
    const orderId = safe(rawOrderId, 'N/A').replace(/^#+/, '');
    doc.text(`Order: #${orderId}`, ML, y);
    const payDate = invoiceData.transaction.payment_date
      ? this.formatReceiptDateTime(
          invoiceData.transaction.payment_date,
          this.resolveReceiptTimezone(invoiceData)
        )
      : 'N/A';
    doc.text(`Date: ${payDate}`, MR, y, { align: 'right' });
    y += 6;

    divider();

    // Bill to
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(PDF_PRIMARY_COLOR);
    doc.text('BILL TO:', ML, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    const billLines = [invoiceData.customerInfo.name, invoiceData.customerInfo.email].filter(
      v => v && v.trim() !== '' && v.trim() !== 'N/A'
    );
    for (const line of billLines) {
      doc.text(line, ML, y);
      y += 4;
    }
    y += 1;

    divider();

    // Items table header
    doc.setFont('helvetica', 'bold');
    doc.text('DESCRIPTION', ML, y);
    doc.text('AMOUNT', MR, y, { align: 'right' });
    y += 3;
    divider();

    doc.setFont('helvetica', 'normal');

    // Main service (amounts computed above, alongside the derived discount)
    doc.text(safe(tx.package_name, 'Car Wash Service'), ML, y);
    doc.text(`$${packagePortion.toFixed(2)}`, MR, y, { align: 'right' });
    y += 5;

    for (const a of addonRows) {
      const count = Math.max(1, Number(a.add_on_count || 1));
      const p = Number(a.price ?? 0);
      const line = isFinite(p) && isFinite(count) ? p * count : 0;
      const name = safe(a.addon_name, 'Add-on').trim() || 'Add-on';
      const label = count > 1 ? `Add-on: ${name} x${count}` : `Add-on: ${name}`;
      doc.text(label, ML, y);
      doc.text(`$${line.toFixed(2)}`, MR, y, { align: 'right' });
      y += 5;
    }

    if (discountNum > 0) {
      const dLabel = tx.voucher_code ? `Discount (${tx.voucher_code})` : 'Discount';
      doc.text(dLabel, ML, y);
      doc.text(`-$${discountNum.toFixed(2)}`, MR, y, { align: 'right' });
      y += 5;
    }

    doc.text('Taxes + Fees', ML, y);
    doc.text('$0.00', MR, y, { align: 'right' });
    y += 4;

    divider();

    const totalAmount = Math.max(0, subtotalBeforeDiscount - discountNum);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('TOTAL', ML, y);
    doc.text(`$${totalAmount.toFixed(2)}`, MR, y, { align: 'right' });
    // Slight gap when promo follows; keep roomier spacing before payment otherwise.
    y += showPromo ? 4 : 6;

    if (showPromo) {
      // Below TOTAL, full content width — align with package / taxes rows (ML..MR).
      y = this.renderPromoMessageBox(doc, promoMessage, ML, y, promoWidth, promoFontSize);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(PDF_PRIMARY_COLOR);
    }

    // Payment method
    const hasPaymentMethod = tx.payment_method && tx.payment_method !== '-';
    if (hasPaymentMethod) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      let pm = this.formatPaymentMethod(tx.payment_method!);
      const card = resolvePaymentReceiptCard(tx, invoiceData.cardDetails);
      const cardLabel = formatCardPaymentLabel(card?.brand, card?.last4);
      if (cardLabel) {
        pm = cardLabel;
      }
      doc.text(`Payment: ${pm}`, ML, y);
      y += 6;
    }

    divider();

    // Footer
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(PDF_SECONDARY_COLOR);
    // 24-hour raincheck promise — single wash purchases only (CWS-1523).
    if (showRaincheck) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(PDF_PRIMARY_COLOR);
      for (const line of doc.splitTextToSize(RAINCHECK_MESSAGE, promoWidth) as string[]) {
        doc.text(line, CX, y, { align: 'center' });
        y += 4;
      }
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(PDF_SECONDARY_COLOR);
      y += 1;
    }
    doc.text(`Drive clean! - ${safe(invoiceData.businessInfo.name, 'us')}`, CX, y, {
      align: 'center',
    });
    y += 4;
    doc.text(safe(invoiceData.businessInfo.email, 'support@way.com'), CX, y, { align: 'center' });

    return doc;
  }

  private buildInvoiceDocument(invoiceData: InvoiceData): { doc: jsPDF; filename: string } {
    const { doc, pageWidth, pageHeight, primaryColor, secondaryColor, lightGray } =
      this.initializePDFDocument();

    // Defensive helpers — jsPDF throws when doc.text() receives null / undefined.
    const safe = (v: string | null | undefined, fallback = ''): string =>
      v != null && String(v).trim() !== '' ? String(v) : fallback;

    // Company Logo and Header
    doc.setFontSize(24);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text(safe(invoiceData.businessInfo.name, 'Car Wash'), 20, 30);

    // Business Info (address removed from header)
    doc.setFontSize(10);
    doc.setTextColor(secondaryColor);
    doc.setFont('helvetica', 'normal');
    // Address removed - no longer displayed in header
    doc.text(
      `Phone: ${PhoneValidator.formatUsaPhone(safe(invoiceData.businessInfo.phone, 'N/A'))}`,
      20,
      40
    );
    doc.text(`Email: ${safe(invoiceData.businessInfo.email, 'N/A')}`, 20, 46);

    // Invoice Title and Details (Right Side)
    doc.setFontSize(20);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageWidth - 20, 30, { align: 'right' });

    doc.setFontSize(10);
    doc.setTextColor(secondaryColor);
    doc.setFont('helvetica', 'normal');
    // Remove Invoice # line - not needed
    // Format Order ID - ensure only one # prefix (remove extra # if present)
    let orderId = invoiceData.order.orderId || '';
    // Remove all leading # characters, then add exactly one
    orderId = orderId.replace(/^#+/, '');
    orderId = `#${orderId}`;
    doc.text(`Order ID: ${orderId}`, pageWidth - 20, 40, { align: 'right' });
    const paymentDateStr = invoiceData.transaction.payment_date
      ? this.formatDate(
          invoiceData.transaction.payment_date,
          this.resolveReceiptTimezone(invoiceData)
        )
      : 'N/A';
    doc.text(`Date: ${paymentDateStr}`, pageWidth - 20, 46, { align: 'right' });

    // Customer Information Section
    // Calculate container height: top padding (10) + BILL TO label (8) + name (6) + email (6) + phone (6) + location (6) + bottom padding (10) = 42
    const customerInfoStartY = 70;
    const customerInfoHeight = 42;
    doc.setFillColor(lightGray);
    doc.rect(20, customerInfoStartY, pageWidth - 40, customerInfoHeight, 'F');

    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('BILL TO:', 25, customerInfoStartY + 10); // 10px padding from top

    doc.setFontSize(10);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'normal');
    let currentInfoY = customerInfoStartY + 18; // BILL TO label + spacing
    const custName = safe(invoiceData.customerInfo.name, 'N/A');
    const custEmail = safe(invoiceData.customerInfo.email, 'N/A');
    const custPhone = PhoneValidator.formatUsaPhone(safe(invoiceData.customerInfo.phone, 'N/A'));
    doc.text(custName, 25, currentInfoY);
    currentInfoY += 6;
    doc.text(custEmail, 25, currentInfoY);
    currentInfoY += 6;
    doc.text(custPhone, 25, currentInfoY);
    currentInfoY += 6;
    // Add location inside the container
    const custAddr = safe(invoiceData.customerInfo.address);
    if (custAddr) {
      doc.text(custAddr, 25, currentInfoY);
    }

    // Services Table Header
    const tableStartY = customerInfoStartY + customerInfoHeight + 8; // 8px spacing after customer info
    let currentY = this.renderTableHeader(doc, pageWidth, tableStartY);

    // Main service + add-ons (reconcile with transaction history API like order details)
    const tx = invoiceData.transaction;
    const amountStr = String(tx.amount || '0');
    const baseAmount = isFinite(Number(amountStr)) ? Number(amountStr) : 0;
    const addonRows = Array.isArray(tx.addons) ? tx.addons : [];
    const addonSum = sumPaymentTransactionAddonAmount(tx);
    const amountIncludesAddons = isPaymentTransactionAmountInclusiveOfAddons(tx);
    const packagePortion = amountIncludesAddons
      ? Math.max(0, baseAmount - addonSum)
      : Math.max(0, baseAmount);
    const subtotalBeforeDiscount = amountIncludesAddons ? baseAmount : baseAmount + addonSum;
    // Derived, not read from tx.discount: see resolvePaymentTransactionDiscount.
    const discountNum = resolvePaymentTransactionDiscount(tx, subtotalBeforeDiscount);

    doc.text(safe(tx.package_name, 'Car Wash Service'), 25, currentY);
    doc.text(`$${packagePortion.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });
    currentY += 8;

    for (const a of addonRows) {
      const count = Math.max(1, Number(a.add_on_count || 1));
      const p = Number(a.price ?? 0);
      const line = isFinite(p) && isFinite(count) ? p * count : 0;
      const name = safe(a.addon_name, 'Add-on').trim() || 'Add-on';
      const label = count > 1 ? `Add-on: ${name} ×${count}` : `Add-on: ${name}`;
      doc.text(label, 25, currentY);
      doc.text(`$${line.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });
      currentY += 8;
    }

    if (discountNum > 0) {
      const dLabel = tx.voucher_code ? `Discount (${tx.voucher_code})` : 'Discount';
      doc.text(dLabel, 25, currentY);
      doc.text(`- $${discountNum.toFixed(2)}`, pageWidth - 25, currentY, { align: 'right' });
      currentY += 8;
    }

    // Taxes and Fees
    doc.text('Taxes + Fees', 25, currentY);
    doc.text('$0.00', pageWidth - 25, currentY, { align: 'right' });
    currentY += 8;

    // Subtotal line
    doc.setLineWidth(0.5);
    doc.setDrawColor(secondaryColor);
    doc.line(25, currentY + 2, pageWidth - 25, currentY + 2);
    currentY += 10;

    const taxes = 0;
    const totalAmount = Math.max(0, subtotalBeforeDiscount - discountNum + taxes);
    const totalFormatted = `$${totalAmount.toFixed(2)}`;

    // Total
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('TOTAL', 25, currentY);
    doc.text(totalFormatted, pageWidth - 25, currentY, { align: 'right' });

    const promoMessage = safe(tx.promo_message);
    const showPromo = hasPromoMessageContent(promoMessage);
    currentY += showPromo ? 6 : 10;

    if (showPromo) {
      // Below TOTAL, full content width — align with package / amount columns (25 .. pageWidth-25).
      const promoWidth = pageWidth - 50;
      currentY = this.renderPromoMessageBox(doc, promoMessage, 25, currentY, promoWidth, 8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(primaryColor);
    }

    // Payment Information Section - Always show payment method
    const hasPaymentMethod =
      invoiceData.transaction.payment_method && invoiceData.transaction.payment_method !== '-';
    const card = resolvePaymentReceiptCard(invoiceData.transaction, invoiceData.cardDetails);
    if (hasPaymentMethod || card?.last4) {
      currentY += 15;
      doc.setFontSize(12);
      doc.setTextColor(primaryColor);
      doc.setFont('helvetica', 'bold');
      doc.text('PAYMENT INFORMATION', 25, currentY);

      currentY += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      // Payment Method - use transaction payment_method if available, otherwise use card details
      let paymentMethodText = 'N/A';
      const cardLabel = formatCardPaymentLabel(card?.brand, card?.last4);
      if (cardLabel) {
        paymentMethodText = cardLabel;
      } else if (hasPaymentMethod) {
        paymentMethodText = this.formatPaymentMethod(invoiceData.transaction.payment_method!);
      }
      doc.text(`Payment Mode: ${paymentMethodText}`, 25, currentY);
      currentY += 6;
    }

    // 24-hour raincheck promise — single wash purchases only (CWS-1523).
    if (shouldShowRaincheckMessage(invoiceData)) {
      currentY += 12;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColor);
      doc.text(RAINCHECK_MESSAGE, pageWidth / 2, currentY, { align: 'center' });
      doc.setFont('helvetica', 'normal');
    }

    // Footer
    const footerY = pageHeight - 30;
    doc.setFontSize(8);
    doc.setTextColor(secondaryColor);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Drive clean! - ${safe(invoiceData.businessInfo.name, 'us')}`,
      pageWidth / 2,
      footerY,
      { align: 'center' }
    );
    doc.text(
      `For support, contact us at ${safe(invoiceData.businessInfo.email, 'support@way.com')}`,
      pageWidth / 2,
      footerY + 6,
      { align: 'center' }
    );

    // Generate filename and download
    // Handle case where payment_intent_id might be null
    const orderIdForFilename = invoiceData.order.orderId || 'any';
    const paymentIdSuffix = invoiceData.transaction.payment_intent_id
      ? invoiceData.transaction.payment_intent_id.slice(-6)
      : 'no-id';
    const filename = `invoice-${orderIdForFilename}-${paymentIdSuffix}.pdf`;
    return { doc, filename };
  }

  private resolveReceiptTimezone(invoiceData: InvoiceData): string {
    return (
      invoiceData.receiptTimezone?.trim() ||
      invoiceData.order.locationTimezone?.trim() ||
      getUserTimezone()
    );
  }

  private formatReceiptDateTime(dateString: string, timeZone: string): string {
    const tz = timeZone.trim() || getUserTimezone();
    return formatDateTimeInTimeZone(dateString, tz) || 'N/A';
  }

  private formatDate(dateString: string, timeZone: string): string {
    return this.formatReceiptDateTime(dateString, timeZone);
  }

  private formatAmountCents(amountInCents: string | number): string {
    const amount = typeof amountInCents === 'string' ? parseFloat(amountInCents) : amountInCents;
    const dollars = amount;
    return `$${dollars.toFixed(2)}`;
  }

  /**
   * Generates and downloads a PDF invoice for a given service record and customer.
   * @example
   * enenerateServiceHistoryInvoice(sampleServiceRecord, { name: 'Jane Doe', email: 'jane@example.com', phone: '555-1234' })
   * undefined
   * @param {{ServiceRecord}} {{serviceRecord}} - The service record containing details like id, date, packageName, price, vehicle, and location.
   * @param {{{name: string; email: string; phone: string; address?: string}}} {{customerInfo}} - Customer contact information used for the invoice billing section.
   * @returns {{void}} Generates and saves a PDF file (invoice-{serviceRecord.id}.pdf) and returns nothing.
   **/
  generateServiceHistoryInvoice(
    serviceRecord: ServiceRecord,
    customerInfo: {
      name: string;
      email: string;
      phone: string;
      address?: string;
    }
  ): void {
    const { doc, pageWidth, pageHeight, primaryColor, secondaryColor, lightGray } =
      this.initializePDFDocument();

    // Company Logo and Header
    doc.setFontSize(24);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('Way.com Car Wash', 20, 30);

    // Business Info (address removed from header)
    doc.setFontSize(10);
    doc.setTextColor(secondaryColor);
    doc.setFont('helvetica', 'normal');
    // Address removed - no longer displayed in header
    doc.text('Phone: (555) 123-4567', 20, 40);
    doc.text('Email: support@way.com', 20, 46);

    // Invoice Title and Details (Right Side)
    doc.setFontSize(20);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageWidth - 20, 30, { align: 'right' });

    doc.setFontSize(10);
    doc.setTextColor(secondaryColor);
    doc.setFont('helvetica', 'normal');
    // Invoice # removed - not needed
    doc.text(`Date: ${serviceRecord.date}`, pageWidth - 20, 40, { align: 'right' });

    // Customer Information Section
    doc.setFillColor(lightGray);
    doc.rect(20, 70, pageWidth - 40, 30, 'F');

    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('BILL TO:', 25, 80);

    doc.setFontSize(10);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'normal');
    doc.text(customerInfo.name, 25, 88);
    doc.text(customerInfo.email, 25, 94);
    doc.text(PhoneValidator.formatUsaPhone(customerInfo.phone || 'N/A'), 25, 100);
    if (customerInfo.address) {
      doc.text(customerInfo.address, 25, 106);
    }

    // Services Table Header
    const tableStartY = 120;
    let currentY = this.renderTableHeader(doc, pageWidth, tableStartY);

    // Main service
    doc.text(serviceRecord.packageName, 25, currentY);
    doc.text(serviceRecord.price, pageWidth - 25, currentY, { align: 'right' });
    currentY += 8;

    // Vehicle information
    doc.text(`Vehicle: ${serviceRecord.vehicle}`, 25, currentY);
    currentY += 8;

    // Location
    doc.text(`Location: ${serviceRecord.location}`, 25, currentY);
    currentY += 8;

    // Taxes and Fees
    doc.text('Taxes + Fees', 25, currentY);
    doc.text('$0.00', pageWidth - 25, currentY, { align: 'right' });
    currentY += 8;

    // Subtotal line
    doc.setLineWidth(0.5);
    doc.setDrawColor(secondaryColor);
    doc.line(25, currentY + 2, pageWidth - 25, currentY + 2);
    currentY += 10;

    // Total
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('TOTAL', 25, currentY);
    doc.text(serviceRecord.price, pageWidth - 25, currentY, { align: 'right' });

    // Footer
    const footerY = pageHeight - 30;
    doc.setFontSize(8);
    doc.setTextColor(secondaryColor);
    doc.setFont('helvetica', 'normal');
    doc.text('Thank you for choosing Way.com Car Wash!', pageWidth / 2, footerY, {
      align: 'center',
    });
    doc.text('For support, contact us at support@way.com', pageWidth / 2, footerY + 6, {
      align: 'center',
    });

    const filename = `invoice-${serviceRecord.id}.pdf`;
    this.outputPdfForDownload(doc, filename);
  }

  private capitalizeFirst(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  /**
   * Format a payment method identifier into a human-readable string.
   * @example
   * formatPaymentMethod('card_present')
   * 'Card'
   * @param {{string | null | undefined}} {{paymentMethod}} - Payment method identifier (may be snake_case or falsy).
   * @returns {{string}} Return formatted payment method or '-' when missing.
   **/
  private formatPaymentMethod(paymentMethod: string): string {
    if (!paymentMethod) return '-';
    if (paymentMethod.toLowerCase() === PaymentMethodCode.CardPresent) {
      return 'Card';
    }
    // Capitalize first letter and replace underscores with spaces
    return paymentMethod
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
